// ============================================================
// FILE: _services/language-preference.service.ts
//
// Shared multilingual-support helper for pages that follow the
// Pregnancy Screening convention (see pages/obstetrics/pregnancy-screening):
// a per-page language dropdown backed by `data/multilanguage.json`, with
// static label text translated on demand via the `multilanguage/transfer`
// backend endpoint and shown alongside the English original.
//
// Pregnancy Screening keeps `language` as component-local state that
// resets to English on every visit. This service adds the piece that
// was missing: the selected language is persisted to localStorage and
// restored automatically the next time the user logs in, so pages that
// inject this singleton (Device Integration, Triage) stay in sync with
// each other and across sessions without each one re-implementing storage.
// ============================================================
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { RolesData } from '../@core/interfaces/common/roles';
import * as multilanguage from '../data/multilanguage.json';

export interface LanguageOption {
  language: string;
  languagecode: string;
}

const LANGUAGE_STORAGE_KEY = 'preferredContentLanguage';
const DEFAULT_LANGUAGE = 'en';

@Injectable({ providedIn: 'root' })
export class LanguagePreferenceService {
  readonly languageData: LanguageOption[] = (multilanguage as any).default;

  private readonly languageSubject = new BehaviorSubject<string>(this.readStoredLanguage());
  readonly language$: Observable<string> = this.languageSubject.asObservable();

  constructor(private rolesService: RolesData) {}

  get currentLanguage(): string {
    return this.languageSubject.value;
  }

  private readStoredLanguage(): string {
    return localStorage.getItem(LANGUAGE_STORAGE_KEY) || DEFAULT_LANGUAGE;
  }

  setLanguage(code: string): void {
    const lang = code || DEFAULT_LANGUAGE;
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
    this.languageSubject.next(lang);
  }

  // Batch-translates `texts` (in order) via the same backend endpoint
  // Pregnancy Screening uses; the response's `translations[0].text` per
  // item lines up positionally with the input array.
  translateTexts(texts: string[], langCode: string): Observable<any> {
    const transferData = texts.map(text => ({ text }));
    return this.rolesService.create(`${environment.apiUrl}/multilanguage/transfer/${langCode}`, transferData);
  }
}
